import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { bookingsState } from 'store/bookings/selectors';
import { Booking } from 'store/bookings/types';
import {
  selectTransactions,
  selectTransactionsLoading,
  selectTransactionsStatus,
  selectTransactionsTotal,
  selectTransactionsNextOffset,
} from 'store/transactions/selectors';
import { TransactionDetails } from 'store/transactions/types';
import { useLocale } from 'hooks/use-locale.hook';
import { getFormattedEventDuration } from 'utils/formatDate';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { getTransactions } from 'store/transactions/actions';
import { ACTION_STATUSES, BOOKINGS_PAGE_SIZE } from 'shared/consts';
import { isHiddenFromList } from './visible-bookings';

interface UseUpcomingBookingsProps {
  setBookingId: React.Dispatch<React.SetStateAction<number | string>>;
  openReceipt: (transactionId?: number | string) => void;
  /** Per-consumer choice: this hook feeds both the My Bookings list and the landing carousel, and
   * only the list was asked to show rejections. On the list it filters nothing -- see `formatTransactions`. */
  includeRejected?: boolean;
}

interface UseUpcomingBookingsReturnValues {
  upcomingBookings: Array<Booking>;
  formatTransactions: Array<TransactionDetails>;
  isUpcomingBookingsLoading: boolean;
  isTransactionsLoading: boolean;
  /** Settled on having nothing to show -- false while the first read is in flight, so no flash of empty. */
  isListEmpty: boolean;
  hasMore: boolean;
  /** A page landing on top of rows already shown, as opposed to the first paint's skeleton. */
  isLoadingMore: boolean;
  /** Ask for the next page. Safe to call on every scroll frame; it no-ops unless one is due. */
  loadMore: () => void;
  renderEventDurationAndLocationString: (
    startAt: string,
    endAt: string,
    locationName: string,
    resourceTimezone: string,
  ) => string;
  handleClick: (id: string) => void;
}

/** Non-fetching twin of `useUpcomingBookings`'s `isListEmpty`. That hook fetches and auto-opens
 * `?bookingId=`, so a second caller would read twice and open the booking twice. */
export const useIsBookingsListEmpty = (): boolean => {
  const transactions = useSelector(selectTransactions);
  const isTransactionsLoading = useSelector(selectTransactionsLoading);

  return !transactions?.length && !isTransactionsLoading;
};

export const useUpcomingBookings = ({
  setBookingId,
  openReceipt,
  includeRejected = false,
}: UseUpcomingBookingsProps): UseUpcomingBookingsReturnValues => {
  const dispatch = useDispatch();
  const isTransactionsLoading = useSelector(selectTransactionsLoading);
  const isUpcomingBookingsLoading = useSelector(bookingsState).loading;
  const locale = useLocale();
  const searchParams = useSearchParams<{ bookingId?: string }>();
  const transactionsStatus = useSelector(selectTransactionsStatus);
  const transactions = useSelector(selectTransactions);
  const total = useSelector(selectTransactionsTotal);
  const hasOpenedBookingRef = useRef(false);
  const hasRequestedFirstPageRef = useRef(false);

  const upcomingBookings = useSelector(bookingsState)
    .bookings.filter(booking => !isHiddenFromList(booking.status, includeRejected))
    .sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());

  // The My Bookings list gets these untouched: the read is server-paged, so dropping a row leaves a
  // hole in a page `total` still counted and re-sorting scrambles rows against the next `offset`.
  const formatTransactions: TransactionDetails[] = useMemo(() => {
    const rows = transactions ?? [];

    return includeRejected ? rows : rows.filter(({ details }) => !isHiddenFromList(details.status, includeRejected));
  }, [transactions, includeRejected]);

  // Not `transactions.length` -- see `transactionsNextOffset` in store/transactions/types.
  const nextOffset = useSelector(selectTransactionsNextOffset);
  const hasMore = total !== null && nextOffset < total;

  // The epic is a `switchMap`, so a second request cancels the one in flight: the loading guard is
  // what stops a per-scroll-frame call from discarding the page it just asked for.
  const loadMore = useCallback(() => {
    if (isTransactionsLoading || !hasMore) {
      return;
    }

    dispatch(getTransactions.request({ offset: nextOffset, limit: BOOKINGS_PAGE_SIZE }));
  }, [dispatch, hasMore, isTransactionsLoading, nextOffset]);

  const handleClick = useCallback(
    (id: string) => {
      setBookingId(id);
      openReceipt(id);
    },
    [openReceipt, setBookingId],
  );

  const renderEventDurationAndLocationString = useCallback(
    (startAt: string, endAt: string, locationName: string, resourceTimezone: string): string => {
      if (locationName) {
        return `${getFormattedEventDuration(startAt, endAt, locale, resourceTimezone)} / ${locationName}`;
      }
      return getFormattedEventDuration(startAt, endAt, locale, resourceTimezone);
    },
    [locale],
  );

  // The ref guards a REJECTED read: it is neither PENDING nor FULFILLED and leaves the list empty, so
  // a status-only condition would fire again the moment it fails. This effect owns the first page
  // only; `loadMore` dispatches the same action for every page after it.
  //
  // RES-1432: FULFILLED is a guard too. The ref lives and dies with this mount, and the landing
  // unmounts this hook's carousel under its skeleton for the whole time the read is loading. For a
  // tenant with no upcoming bookings, every answer is an empty list: it remounts the carousel with a
  // fresh ref, which asks again, which brings the skeleton back, and the loop never ends.
  // A settled, empty first page is an answer, not a reason to ask again.
  useEffect(() => {
    if (
      hasRequestedFirstPageRef.current ||
      transactionsStatus === ACTION_STATUSES.PENDING ||
      transactionsStatus === ACTION_STATUSES.FULFILLED ||
      transactions?.length
    ) {
      return;
    }

    hasRequestedFirstPageRef.current = true;
    dispatch(getTransactions.request({ offset: 0, limit: BOOKINGS_PAGE_SIZE }));
  }, [dispatch, transactionsStatus, transactions]);

  useEffect(() => {
    if (searchParams.bookingId && !hasOpenedBookingRef.current && transactionsStatus === ACTION_STATUSES.FULFILLED) {
      hasOpenedBookingRef.current = true;
      setBookingId(searchParams.bookingId);
      openReceipt(searchParams.bookingId);
    }
  }, [searchParams, setBookingId, openReceipt, transactionsStatus]);

  return {
    upcomingBookings,
    isUpcomingBookingsLoading,
    formatTransactions,
    isTransactionsLoading,
    isListEmpty: !formatTransactions?.length && !isTransactionsLoading,
    hasMore,
    isLoadingMore: isTransactionsLoading && Boolean(transactions?.length),
    loadMore,
    renderEventDurationAndLocationString,
    handleClick,
  };
};
